import React, {useEffect, useState} from "react";
import {Switch} from "../ui/switch";
import {MultiSelectCombobox} from "../ui/comboBox";
import {useForm} from "react-hook-form";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../ui/form";
import {Input} from "../ui/input";
import {fetchWithToken, postWithToken, putWithToken} from "../../api/apiBase";
import {useParams} from "react-router-dom";
import {useAuthInfo} from "@propelauth/react";
import {CreateRound, Eligibility, RoundModel} from "./round_model";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import {Separator} from "../ui/separator";
import {
    AmountViewMode,
    BidConfig,
    BiddingView,
    NameViewMode,
} from "@/src/components/bid/bidding_model";
import {SampleData, makeCard} from "../builder/AuctionStatusView";
import {Button} from "../ui/button";
import {Info} from "lucide-react";
import {CardHeader, CardTitle} from "../ui/card";
import {deleteRound} from "../../api/round_api";
import {useToast} from "../ui/use-toast";


type RoundProps = {
    round: RoundModel;
    validate?: (round: RoundModel) => boolean;
    isFirstRound: boolean;
    index: number;
    setIsRoundDeleted: React.Dispatch<React.SetStateAction<boolean>>;
    SetUpdateRounds: React.Dispatch<React.SetStateAction<RoundModel[]>>;
};



function getDifferenceInDays(date1: any, date2: any) {
    if(!date1 || !date2){
        return NaN
    }
    const firstDate = new Date(date1);
    const secondDate = new Date(date2);

    // @ts-ignore
    const differenceInMilliseconds = secondDate - firstDate;
    // if(isNaN(differenceInMilliseconds)){
    //     return null
    // }

    // milliseconds to days
    const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);

    return differenceInDays;
}

function addDaysToDate(date: any, daysToAdd: number) {
    const resultDate = new Date(date);
    resultDate.setDate(resultDate.getDate() + daysToAdd);
    return resultDate.toISOString().split('T')[0];
}


function _checkActiveRound(round: RoundModel) {
    const currentTime = new Date().getTime();
    const roundStartTime = round.start_date
        ? new Date(round.start_date).getTime()
        : null;
    const roundEndTime = round.end_date
        ? new Date(round.end_date).getTime()
        : null;

    if (!roundStartTime ){
        return false;
    } else if (!roundEndTime) {
        return roundStartTime <= currentTime;
    } else {
        return roundStartTime <= currentTime && currentTime < roundEndTime
    }
    // return false;
}


/**
 * This is for configuring a single period
 *
 *
 *
 * */
export function RoundConfigurator_v2({round, setIsRoundDeleted, SetUpdateRounds, index}: RoundProps) {

    const {accessToken} = useAuthInfo();
    const methods = useForm<RoundModel>({
        defaultValues: round,
    });
    const [w_name, w_amount] = methods.watch([
        "bid_config.bid_view_mode.names",
        "bid_config.bid_view_mode.amounts",
    ]);
    const fakeData = SampleData({names: w_name, amounts: w_amount});
    const [isActiveRound, setIsActiveRound] = useState(false);
    const {toast} = useToast();
    const isPast = !!round.end_date && (new Date(round.end_date).getTime() < new Date().getTime());
    useEffect(() => {
        const checkActiveRound = () => {
            const active = _checkActiveRound(round);
            if (active !== isActiveRound) {
                setIsActiveRound(active);
            }
        };
        checkActiveRound();
        const intervalId = setInterval(checkActiveRound, 50000);

        return () => clearInterval(intervalId);
    }, [round.start_date, round.end_date]);

    const handleChangeValue = (fieldName: string) => {
        return (event: React.FocusEvent<HTMLInputElement> | string | null) => {
            let newValue: string | null;
            if (typeof event === "string") {
                newValue = event;
            } else if (event && event.target) {
                newValue = event.target.value;
            } else {
                newValue = null;
            }
            //  console.log(newValue, "New value");

            SetUpdateRounds((prevRounds: RoundModel[]) => {
                const updatedRounds = [...prevRounds];

                if (fieldName === "end_date") {
                    // date handling
                    //
                    const delta = getDifferenceInDays(updatedRounds[index].end_date, newValue);
                    updatedRounds[index].end_date = newValue;

                    if (newValue && index < updatedRounds.length - 1) {

                        for (let i = index + 1; i < prevRounds.length; i++) {
                            const next_start = updatedRounds[i].start_date
                            const next_end = updatedRounds[i].end_date
                            if (next_start) {
                                //
                                updatedRounds[i].start_date = addDaysToDate(next_start, delta);
                            } else {
                                // we got to a round without a start
                                // set this
                                updatedRounds[i].start_date = newValue;

                            }
                            // @ts-ignore
                            if (next_end && newValue > next_end) {
                                updatedRounds[i].end_date = addDaysToDate(next_end, delta);
                            } else {
                                // we get to a round without an end,
                                // or the new date does not affect this round - we break
                                break;
                            }
                        }
                    } else if (!newValue) {
                        // if we set a duration to undefined - that will blow up everyone
                        for (let i = index + 1; i < prevRounds.length; i++) {
                            // @ts-ignore
                            updatedRounds[i].start_date = null;
                            updatedRounds[i].end_date = null;
                        }
                    }
                    return updatedRounds;

                } else if (fieldName === "bid_config.bid_view_mode.names") {
                    updatedRounds[index].bid_config.bid_view_mode.names = newValue as NameViewMode;
                    return updatedRounds;

                } else if (fieldName === "bid_config.bid_view_mode.amounts") {
                    updatedRounds[index].bid_config.bid_view_mode.amounts = newValue as AmountViewMode;
                    return updatedRounds;
                } else {
                    //
                    updatedRounds[index] = {...updatedRounds[index], ...{[fieldName]: newValue}};
                    return updatedRounds;
                }
            });
        };
    };

    const handleSubmit = (data: RoundModel) => {};

    const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        const formData = methods.getValues();
        deleteRound(formData.project_id, formData.id, accessToken as string)
            .then(() => {
                toast({
                    title: "Success!",
                    description: "Round deleted successfully.",
                    variant: "default",
                });
                setIsRoundDeleted(true);
            })
            .catch((e) => {
                console.log(e);
                toast({
                    title: "Error!",
                    description: "Failed to delete the round.",
                    variant: "destructive",
                });
            });
    };

    const duration =  () => {
        const dur = getDifferenceInDays(round.start_date, round.end_date)
        if(isNaN(dur) || dur < 0){
            return "?"
        } else {
            return `${dur} ${dur===1 ? "Day" : "Days"}`;
        }
    }

    function formatStartDate(date:string) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return
        }
        const [year, month, day] = date.split('-'); 
        return `${month}/${day}/${year}`;
    }

    return (
        <div className="p-4 bg-white rounded shadow-md max-w-6xl mx-auto ">
            {/*TODO - PREVIOUS PHASES      mx-4*/}
            <Form {...methods}>
                <form onSubmit={methods.handleSubmit(handleSubmit)}>
                    <div className="relative">
                        <div className="absolute flex gap-3 right-0 bottom-12 ">
                        
                        </div>
                    </div>

                    <div className="flex columns-4 gap-x-10 gap-y-4">
                        <div className="w-2/12 flex flex-col">
                            <div className="flex">{formatStartDate(round.start_date)}</div>
                            <div className="flex mb-2">#{round.index} {isActiveRound ? "active" : ""}</div>
                            {/* - IF THIS IS CURRENT PERIOD - then START DATE IS DISABLED*/}
                            <Button
                                onClick={handleDelete}
                                disabled={index === 0 || isPast || isActiveRound}
                                className={`hover:bg-red-200 ${isActiveRound ? "bg-slate-300" : undefined}`}
                                variant="outline"
                            >
                                Delete
                            </Button>

                            <div className="flex mt-2">Duration: {duration()}</div>
                            <div className="flex justify-end mt-auto -mr-6">
                            <FormField
                                name={"end_date"}
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>End Date</FormLabel>
                                        <FormControl>
                                            <Input
                                                className="w-full"
                                                type={"date"}
                                                min={round.start_date}
                                                {...field}
                                                value={round.end_date || ""}
                                                //disabled={false}
                                                onChange={(e) => {
                                                    handleChangeValue("end_date")(e.target.value);
                                                }}
                                            />
                                            
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            </div>
                        </div>

                        <div className="flex w-4/12 ">
                            <div className="w-full">
                                <FormField
                                    name="name"
                                    render={({field}) => (
                                        <FormItem>
                                            <FormLabel className="flex columns-2 justify-between">
                                                <div>Round Title</div>
                                                <Info className="ml-2 h-4 w-4"/>
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    className="w-full "
                                                    placeholder="Round Title"
                                                    {...field}
                                                    disabled={isPast}
                                                    value={field.value || ""}
                                                    onBlur={handleChangeValue(field.name)}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={methods.control}
                                    name={"eligibility"}
                                    render={({field}) => (
                                        <FormItem>
                                            <FormLabel className="flex columns-2 justify-between mt-[6px] mb-2.5">
                                                <div>Eligibility</div>
                                                <Info className="ml-2 h-4 w-4"/>
                                            </FormLabel>

                                            <Select
                                                disabled={isPast}
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                    handleChangeValue(field.name)(value);
                                                }}
                                                defaultValue={field.value}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder=""/>
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="Closed">Closed - Not Accepting
                                                        Bids</SelectItem>
                                                    <SelectItem value="InviteOnly"> Open to Invited Bidders
                                                        Only</SelectItem>
                                                    {/*TODO - These are conditional on the project being discoverable*/}
                                                    <SelectItem value="OpenOnRequest">Open On Request</SelectItem>
                                                    <SelectItem value="OpenToAll"> Open To All</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>


                        {/*FEEDBACK Chunk*/}
                        <div className="flex w-3/12">

                            <Separator orientation="vertical" className="mr-12"/>
                            <div className="w-full">
                                <CardTitle>Feedback Settings</CardTitle>
                                <Separator/>
                                <FormField
                                    control={methods.control}
                                    name={"bid_config.bid_view_mode.names"}
                                    render={({field}) => (
                                        <FormItem>
                                            <FormLabel >Names</FormLabel>
                                            <Select
                                                disabled={isPast}
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                    handleChangeValue(
                                                        "bid_config.bid_view_mode.names"
                                                    )(value);
                                                }}
                                                defaultValue={field.value}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder=""/>
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="hidden">Hidden</SelectItem>
                                                    <SelectItem value="visible">Visible</SelectItem>
                                                    <SelectItem value="detached">Detached</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={methods.control}
                                    name={"bid_config.bid_view_mode.amounts"}
                                    render={({field}) => (
                                        <FormItem>
                                            <FormLabel className="!m-0 !p-0">Amounts</FormLabel>
                                            <Select
                                                disabled={isPast}
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                    handleChangeValue(
                                                        "bid_config.bid_view_mode.amounts"
                                                    )(value);
                                                }}
                                                defaultValue={field.value}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue
                                                            placeholder="Select a verified email to display"/>
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="visible">Visible</SelectItem>
                                                    <SelectItem value="ranked">Ranked</SelectItem>
                                                    <SelectItem value="hidden">Hidden</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                        <div className="flex w-5/12">
                            <Separator orientation="vertical" className="mr-12"/>
                            <div className="w-full">
                                <CardTitle>What the Bidder sees:</CardTitle>
                                <Separator/>
                                <div className="mt-4">{makeCard(fakeData)}</div>
                            </div>
                        </div>
                    </div>
                </form>
            </Form>
        </div>

    );
}
